import React, { useState, useMemo } from 'react';
import { PersonnelConfig, Service, Competence } from '../../../../../../shared/types/config';

interface PersonnelModalProps {
  editingPersonnel: PersonnelConfig | null;
  personnelForm: Omit<PersonnelConfig, 'id'>;
  setPersonnelForm: React.Dispatch<React.SetStateAction<Omit<PersonnelConfig, 'id'>>>;
  services: Service[];
  competences: Competence[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const PersonnelModal: React.FC<PersonnelModalProps> = ({
  editingPersonnel,
  personnelForm,
  setPersonnelForm,
  services,
  competences,
  onClose,
  onSave,
}) => {
  const [searchCompetence, setSearchCompetence] = useState('');

  /** Uniquement compétences générales + celles du même service que le grade (pas les autres services). */
  const filteredAndGroupedCompetences = useMemo(() => {
    const sid = personnelForm.id_service;
    let filtered = competences.filter((competence) => {
      if (!sid || sid <= 0) return false;
      if (competence.id_service !== null && competence.id_service !== sid) return false;
      if (searchCompetence) {
        const q = searchCompetence.toLowerCase();
        const matchesSearch =
          competence.nom.toLowerCase().includes(q) ||
          (competence.description && competence.description.toLowerCase().includes(q));
        if (!matchesSearch) return false;
      }
      return true;
    });

    const competencesByService = filtered.reduce((acc, competence) => {
      const serviceId = competence.id_service || 0;
      const serviceName = competence.id_service
        ? services.find(s => s.id === competence.id_service)?.nom || `Service ${serviceId}`
        : 'Générales (tous services)';

      if (!acc[serviceName]) {
        acc[serviceName] = [];
      }
      acc[serviceName].push(competence);
      return acc;
    }, {} as Record<string, Competence[]>);

    Object.keys(competencesByService).forEach(serviceName => {
      competencesByService[serviceName].sort((a, b) => {
        const ordreA = a.ordre ?? 999999;
        const ordreB = b.ordre ?? 999999;
        if (ordreA !== ordreB) {
          return ordreA - ordreB;
        }
        return a.nom.localeCompare(b.nom);
      });
    });

    const serviceNames = Object.keys(competencesByService).sort((a, b) => {
      if (a === 'Générales (tous services)') return -1;
      if (b === 'Générales (tous services)') return 1;
      return a.localeCompare(b);
    });

    return { competencesByService, serviceNames, filtered };
  }, [competences, services, personnelForm.id_service, searchCompetence]);

  const handleSelectAll = () => {
    const filteredComps = filteredAndGroupedCompetences.filtered;
    const allSelected = filteredComps.every(c => personnelForm.id_competences.includes(c.id!));
    if (allSelected) {
      setPersonnelForm({
        ...personnelForm,
        id_competences: personnelForm.id_competences.filter(id => !filteredComps.some(c => c.id === id)),
      });
    } else {
      const newIds = filteredComps.map(c => c.id!).filter(id => id && !personnelForm.id_competences.includes(id));
      setPersonnelForm({
        ...personnelForm,
        id_competences: [...personnelForm.id_competences, ...newIds],
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-2xl font-bold text-white">
            {editingPersonnel ? 'Modifier le personnel' : 'Ajouter un personnel'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Service du grade *</label>
              <select
                value={personnelForm.id_service}
                onChange={(e) => {
                  const nextSid = parseInt(e.target.value) || 0;
                  const validCompetences =
                    nextSid > 0
                      ? personnelForm.id_competences.filter((cid) => {
                          const c = competences.find((x) => x.id === cid);
                          return c && (c.id_service == null || c.id_service === nextSid);
                        })
                      : personnelForm.id_competences;
                  setPersonnelForm({
                    ...personnelForm,
                    id_service: nextSid,
                    id_competences: validCompetences,
                  });
                }}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Sélectionner un service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.nom}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Les grades et leurs compétences sont isolés par service : une fois le service choisi, seules les
                compétences de ce service (et les compétences générales) sont proposées.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Grade *</label>
              <input
                type="text"
                value={personnelForm.grade}
                onChange={(e) => setPersonnelForm({ ...personnelForm, grade: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Prix *</label>
              <input
                type="number"
                value={personnelForm.prix}
                onChange={(e) => setPersonnelForm({ ...personnelForm, prix: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Rang du grade dans ce service</label>
              <input
                type="number"
                value={personnelForm.ordre ?? 0}
                onChange={(e) =>
                  setPersonnelForm({
                    ...personnelForm,
                    ordre: parseInt(e.target.value, 10) || 0,
                  })
                }
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Hiérarchie des grades pour <strong className="text-gray-400">ce service uniquement</strong> : plus
                petit = plus haut / affiché en premier.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Compétences *
                {personnelForm.id_competences.length > 0 && (
                  <span className="ml-2 text-xs text-blue-400">
                    ({personnelForm.id_competences.length} sélectionnée{personnelForm.id_competences.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>

              {personnelForm.id_service > 0 && competences.length > 8 && (
                <div className="mb-3 relative">
                  <input
                    type="text"
                    placeholder="Rechercher une compétence..."
                    value={searchCompetence}
                    onChange={(e) => setSearchCompetence(e.target.value)}
                    className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              )}

              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                {!personnelForm.id_service ? (
                  <p className="text-amber-400/95 text-sm">
                    Choisissez d&apos;abord le service pour afficher les compétences rattachées à ce service (et les
                    compétences générales).
                  </p>
                ) : competences.length === 0 ? (
                  <p className="text-gray-400 text-sm">Aucune compétence disponible</p>
                ) : filteredAndGroupedCompetences.filtered.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">
                    Aucune compétence pour ce service. Créez des compétences dans l&apos;onglet Compétences (liées à ce
                    service ou générales).
                  </p>
                ) : (
                  <div className="space-y-4">
                    {filteredAndGroupedCompetences.serviceNames.map(serviceName => (
                      <div key={serviceName}>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 pb-1 border-b border-gray-700">
                          {serviceName}
                        </div>
                        <div className="space-y-1">
                          {filteredAndGroupedCompetences.competencesByService[serviceName].map(competence => (
                            <label
                              key={competence.id}
                              className="flex items-center gap-2 text-gray-300 hover:text-white cursor-pointer p-2 rounded hover:bg-gray-700/50"
                            >
                              <input
                                type="checkbox"
                                checked={personnelForm.id_competences.includes(competence.id!)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    if (competence.id) {
                                      setPersonnelForm({
                                        ...personnelForm,
                                        id_competences: [...personnelForm.id_competences, competence.id],
                                      });
                                    }
                                  } else {
                                    setPersonnelForm({
                                      ...personnelForm,
                                      id_competences: personnelForm.id_competences.filter(id => id !== competence.id),
                                    });
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <span className="text-sm">{competence.nom}</span>
                                {competence.is_specialite && (
                                  <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">
                                    Spécialité
                                  </span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {personnelForm.id_service > 0 && filteredAndGroupedCompetences.filtered.length > 0 && !searchCompetence && (
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {filteredAndGroupedCompetences.filtered.every((c) => personnelForm.id_competences.includes(c.id!))
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            {editingPersonnel ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonnelModal;

